/**
 * Continuous score → colour scale for the group-by heatmap.
 *
 * A red→amber→green ramp across score 0→1, using the same hues as `getScoreColor`
 * (hooks/useEvaluationData.ts) but interpolated so neighbouring cells are
 * distinguishable. Cells also print their numeric score, so the grid stays
 * readable without relying on the red↔green hue (red-green colour-blindness
 * backstop).
 */

interface Stop {
  at: number;
  rgb: [number, number, number];
}

// 0.0 → #ef4444 (red-500) · 0.5 → #f59e0b (amber-500) · 1.0 → #10b981 (emerald-500)
const STOPS: Stop[] = [
  { at: 0, rgb: [0xef, 0x44, 0x44] },
  { at: 0.5, rgb: [0xf5, 0x9e, 0x0b] },
  { at: 1, rgb: [0x10, 0xb9, 0x81] },
];

/** CSS gradient mirroring STOPS, for the heatmap legend bar. */
export const SCORE_GRADIENT_CSS =
  "linear-gradient(to right, #ef4444 0%, #f59e0b 50%, #10b981 100%)";

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

const hex2 = (n: number): string => Math.round(n).toString(16).padStart(2, "0");

/**
 * Map a score in [0, 1] to a hex colour on the red→amber→green ramp.
 * Out-of-range scores are clamped.
 */
export function scoreColorScale(score: number): string {
  const s = clamp01(score);

  // Find the segment [lo, hi] that contains s.
  let lo = STOPS[0];
  let hi = STOPS[STOPS.length - 1];
  for (let i = 0; i < STOPS.length - 1; i++) {
    if (s >= STOPS[i].at && s <= STOPS[i + 1].at) {
      lo = STOPS[i];
      hi = STOPS[i + 1];
      break;
    }
  }

  const span = hi.at - lo.at;
  const t = span === 0 ? 0 : (s - lo.at) / span;
  const r = lo.rgb[0] + (hi.rgb[0] - lo.rgb[0]) * t;
  const g = lo.rgb[1] + (hi.rgb[1] - lo.rgb[1]) * t;
  const b = lo.rgb[2] + (hi.rgb[2] - lo.rgb[2]) * t;
  return `#${hex2(r)}${hex2(g)}${hex2(b)}`;
}

/**
 * Pick black or white text for legibility on a hex background (luminance
 * heuristic), so the in-cell score stays readable across the whole ramp.
 */
export function readableTextColor(hex: string): string {
  const m = hex.replace("#", "");
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 150 ? "#111827" : "#ffffff";
}
